<template>
  <div class="block-stack flex flex-col h-full w-full items-center justify-center gap-4">
    <div v-if="gameOver" class="text-center mb-2">
      <p class="text-2xl font-bold text-[#1c4848]">Game Over</p>
      <p class="text-[#1c4848]/70">Score: {{ score }} · Lines: {{ linesCleared }}</p>
    </div>
    <div
      ref="gridWrap"
      class="block-stack-grid rounded-xl overflow-hidden border-2 border-[#1c4848]/20 bg-[#1c4848]/5"
      :style="{ '--cols': config.gridCols, '--rows': config.gridRows, '--cell': cellPx + 'px' }"
    >
      <div
        v-for="(row, ri) in displayGrid"
        :key="ri"
        class="block-stack-row flex"
        :style="{ height: cellPx + 'px' }"
      >
        <div
          v-for="(cell, ci) in row"
          :key="ci"
          class="block-stack-cell border border-[#1c4848]/10"
          :style="{ width: cellPx + 'px', height: cellPx + 'px', backgroundColor: cell ? COLORS[cell] : 'transparent' }"
        />
      </div>
    </div>
    <div class="flex gap-4 items-center">
      <button
        type="button"
        class="w-14 h-14 rounded-2xl bg-[#1c4848] text-white flex items-center justify-center shadow-lg hover:bg-[#153838] disabled:opacity-50"
        :disabled="gameOver"
        @click="move(-1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="text-center min-w-[100px]">
        <p class="text-2xl font-bold font-mono text-[#1c4848]">{{ score }}</p>
        <p class="text-xs text-[#1c4848]/50">Lines: {{ linesCleared }}</p>
      </div>
      <button
        type="button"
        class="w-14 h-14 rounded-2xl bg-[#1c4848] text-white flex items-center justify-center shadow-lg hover:bg-[#153838] disabled:opacity-50"
        :disabled="gameOver"
        @click="move(1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
      </button>
    </div>
    <p class="text-xs text-[#1c4848]/50">Left / Right to move. Rows clear when full.</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { parseBlockStackConfig } from './parseGameConfig.js';

const SHAPES = {
  O: { matrix: [[1, 1], [1, 1]], color: 1 },
  I: { matrix: [[1, 1, 1, 1]], color: 2 },
  L: { matrix: [[1, 0], [1, 0], [1, 1]], color: 3 },
  J: { matrix: [[0, 1], [0, 1], [1, 1]], color: 4 },
  T: { matrix: [[1, 1, 1], [0, 1, 0]], color: 5 },
  S: { matrix: [[0, 1, 1], [1, 1, 0]], color: 6 },
  Z: { matrix: [[1, 1, 0], [0, 1, 1]], color: 7 },
};
const SHAPE_KEYS = Object.keys(SHAPES);
const COLORS = ['', '#ecd79f', '#1e88e5', '#e65100', '#43a047', '#9c27b0', '#00bcd4', '#f9a825'];

const props = defineProps({ game: { type: Object, required: true } });
const emit = defineEmits(['stats']);

const config = computed(() => parseBlockStackConfig(props.game?.config));
const cellPx = computed(() => Math.min(28, Math.floor(320 / Math.max(config.value.gridCols, config.value.gridRows))));

const grid = ref([]);
const current = ref(null);
const score = ref(0);
const linesCleared = ref(0);
const dropInterval = ref(1000);
const gameOver = ref(false);
let dropTimer = null;
let totalLinesForSpeed = 0;

function initGrid() {
  const rows = config.value.gridRows;
  const cols = config.value.gridCols;
  grid.value = Array.from({ length: rows }, () => Array(cols).fill(0));
}

function pickShape() {
  const key = SHAPE_KEYS[Math.floor(Math.random() * SHAPE_KEYS.length)];
  const { matrix, color } = SHAPES[key];
  const cols = config.value.gridCols;
  const spawnCol = Math.max(0, Math.floor((cols - matrix[0].length) / 2));
  return { matrix, color, row: 0, col: spawnCol };
}

function mergeGrid() {
  const rows = config.value.gridRows;
  const cols = config.value.gridCols;
  const out = grid.value.map((r) => r.slice());
  const piece = current.value;
  if (!piece) return out;
  const { matrix, color, row, col } = piece;
  for (let r = 0; r < matrix.length; r++) {
    for (let c = 0; c < matrix[r].length; c++) {
      if (matrix[r][c]) {
        const gr = row + r;
        const gc = col + c;
        if (gr >= 0 && gr < rows && gc >= 0 && gc < cols) out[gr][gc] = color;
      }
    }
  }
  return out;
}

const displayGrid = computed(() => mergeGrid());

function collides(dr = 0, dc = 0) {
  const piece = current.value;
  if (!piece) return true;
  const { matrix, row, col } = piece;
  const rows = config.value.gridRows;
  const cols = config.value.gridCols;
  const g = grid.value;
  for (let r = 0; r < matrix.length; r++) {
    for (let c = 0; c < matrix[r].length; c++) {
      if (!matrix[r][c]) continue;
      const gr = row + r + dr;
      const gc = col + c + dc;
      if (gc < 0 || gc >= cols) return true;
      if (gr >= rows) return true;
      if (gr >= 0 && g[gr][gc]) return true;
    }
  }
  return false;
}

function move(dc) {
  if (gameOver.value || !current.value) return;
  if (collides(0, dc)) return;
  current.value = { ...current.value, col: current.value.col + dc };
}

function lock() {
  const piece = current.value;
  if (!piece) return;
  const { matrix, color, row, col } = piece;
  const g = grid.value;
  const rows = config.value.gridRows;
  const cols = config.value.gridCols;
  for (let r = 0; r < matrix.length; r++) {
    for (let c = 0; c < matrix[r].length; c++) {
      if (matrix[r][c]) {
        const gr = row + r;
        const gc = col + c;
        if (gr >= 0 && gr < rows && gc >= 0 && gc < cols) g[gr][gc] = color;
      }
    }
  }
  let cleared = 0;
  for (let ri = rows - 1; ri >= 0; ri--) {
    if (g[ri].every((v) => v !== 0)) {
      g.splice(ri, 1);
      g.unshift(Array(cols).fill(0));
      cleared++;
      ri++;
    }
  }
  if (cleared > 0) {
    linesCleared.value += cleared;
    totalLinesForSpeed += cleared;
    score.value += cleared * 100 * cleared;
    const perSpeed = config.value.linesPerSpeedUp;
    const mult = config.value.speedMultiplier;
    const minI = config.value.minDropInterval * 1000;
    const steps = Math.floor(totalLinesForSpeed / perSpeed);
    dropInterval.value = Math.max(minI, (config.value.initialDropInterval * 1000) * Math.pow(mult, steps));
  }
  current.value = pickShape();
  if (collides(0, 0)) {
    gameOver.value = true;
    current.value = null;
  }
  emit('stats', { score: score.value, linesCleared: linesCleared.value });
}

function tick() {
  if (gameOver.value) return;
  if (collides(1, 0)) {
    lock();
    return;
  }
  current.value = { ...current.value, row: current.value.row + 1 };
}

function scheduleDrop() {
  if (gameOver.value) return;
  dropTimer = setTimeout(() => {
    tick();
    scheduleDrop();
  }, dropInterval.value);
}

function start() {
  initGrid();
  score.value = 0;
  linesCleared.value = 0;
  totalLinesForSpeed = 0;
  gameOver.value = false;
  dropInterval.value = config.value.initialDropInterval * 1000;
  current.value = pickShape();
  if (collides(0, 0)) {
    gameOver.value = true;
    current.value = null;
  }
  emit('stats', { score: 0, linesCleared: 0 });
  scheduleDrop();
}

function restart() {
  if (dropTimer) clearTimeout(dropTimer);
  start();
}

defineExpose({ restart });

onMounted(() => start());
onBeforeUnmount(() => {
  if (dropTimer) clearTimeout(dropTimer);
});

watch(() => [props.game?.id, config.value.gridCols, config.value.gridRows], () => {
  if (dropTimer) clearTimeout(dropTimer);
  start();
}, { deep: true });
</script>

<style scoped>
.block-stack-grid {
  display: flex;
  flex-direction: column;
}
.block-stack-row {
  display: flex;
}
.block-stack-cell {
  flex-shrink: 0;
  box-sizing: border-box;
}
</style>
